import * as global from 'brokertslib/src/helpers/global'
import { Button, FormControl, FormLabel, Switch, useDisclosure } from '@chakra-ui/react'
import { FormProvider, useForm } from 'react-hook-form'

import {
  FormSection,
  FormSectionBody,
  FormSectionDescription,
  FormSectionHeader,
  FormSectionTitle,
} from '../../components/FormSection'
import { MainContent } from '../../components/MainContent'
import { FormButtons } from '../FormButtons'
import { useGetStep4 } from '../../hooks/useWizard'
import { Save } from '../../icons'
import { AdditionalInsureeCestovkoInfo } from '../AdditionalInsureeCestovkoInfo'
import { AdditionalInsurees } from './AdditionalInsurees'
import { InitialPersonAdditionalData, WizardTravelAdditionalData } from 'BrokerTsLib/src/dtos'
import { useInsuranceParamsContext } from '../../layouts/Wizard'
import { useEffect, useState } from 'react'
import ErrorSummary from '../../components/ErrorSummary'
import SubmitDialog from 'BrokerTsLib/src/components/SubmitDialog'
import { Loading } from '../../components/Loading'
import { nameof } from 'ts-simple-nameof'
import { Selection } from 'brokertslib/src/enums'
import { FormSpacer } from '../../components/FormSpacer'
import { SwitchControl } from '../../components/SwitchControl'
import { AdditionalVehicleAssistanceInfo } from './AdditionalVehicleAssistanceInfo'

interface Step4FormProps {
  onSubmit: Function
}

export const Step4Form = ({ onSubmit }: Step4FormProps) => {
  const { insuranceParamsId, insuranceParams } = useInsuranceParamsContext()
  const isContract = !!insuranceParams?.generalParams.contractNumber
  const { isLoading, isFetching, data: axiosResponse, refetch } = useGetStep4(insuranceParamsId!, {
    enabled: false,
    cacheTime: 0,
    onSuccess: (values) => {
      reset(values.data)
    }
  })
  const data = axiosResponse?.data as WizardTravelAdditionalData

  useEffect(() => {
    refetch()
  }, []);

  const {
    isOpen: isSavedFormAlertOpen,
    onOpen: savedFormAlertOnOpen,
    onClose: savedFormAlertOnClose,
  } = useDisclosure()

  const methods = useForm<WizardTravelAdditionalData>({
    defaultValues: {
      policyHolder: InitialPersonAdditionalData,
      // ...data
    }
  })

  const {
    handleSubmit,
    reset,
    formState: { isSubmitting, errors },
    setError,
    watch,
    clearErrors,
    setValue } = methods

  const submitForm = handleSubmit(values => {
    return onSubmit(values)
      .then(() => {
        if (values.nextPage == 'current') {
          savedFormAlertOnOpen()
          refetch()
        }
      })
      .catch((err: any) => {
        if (!(err.response && err.response.status === 400 && err.response.data))
          return;

        let data = err.response.data
        // zobrazeni serverove validace
        Object.keys(data).forEach((x: any) => {
          setError(global.toCamelCase(x), { message: data[x].join('. ') })
        })
      })
  })

  const [isSubmittingCurrent, setIsSubmittingCurrent] = useState(false);
  const [isSubmittingNext, setIsSubmittingNext] = useState(false);

  useEffect(() => {
    if (!isSubmitting) {
      setIsSubmittingCurrent(false)
      setIsSubmittingNext(false)
    }
  }, [isSubmitting]);

  const phType = watch('policyHolder.type')
  const insurees = watch('insurees') ?? []

  return (
    <FormProvider {...methods}>
      <form onSubmit={submitForm}>
        <MainContent>
          <FormSection>
            <FormSectionHeader>
              <FormSectionTitle>Doplňující informace o pojistníkovi</FormSectionTitle>

              <FormSectionDescription>
                Doplňte informace o pojistníkovi a dalších osobách pro sjednání pojištění.
              </FormSectionDescription>
            </FormSectionHeader>

            {!data?.policyHolder && (isLoading || isFetching) && <Loading large={true} />}
            {data?.policyHolder &&
              <FormSectionBody>
                <AdditionalInsureeCestovkoInfo />

                {insurees && [Selection.Person, Selection.SelfEmployed].includes(phType) &&
                  <>
                    <FormControl>
                      <SwitchControl>
                        <Switch
                          checked={insurees.findIndex(x => x.isPolicyHolder) >= 0}
                          defaultChecked={insurees.findIndex(x => x.isPolicyHolder) >= 0}
                          isDisabled={true}
                        />

                        <FormLabel variant="switch">
                          Je zároveň pojištěnou osobou
                        </FormLabel>
                      </SwitchControl>
                    </FormControl>

                    <FormSpacer />
                  </>
                }

              </FormSectionBody>
            }
          </FormSection>

          <FormSection>
            <FormSectionHeader>
              <FormSectionTitle>Pojištěné osoby</FormSectionTitle>

              <FormSectionDescription>Doplňte informace o dalších osobách.</FormSectionDescription>
            </FormSectionHeader>

            <FormSectionBody>
              <AdditionalInsurees prefix={nameof<WizardTravelAdditionalData>(x => x.insurees)} editableBirthnumber />
            </FormSectionBody>
          </FormSection>

          {data?.vehicleData?.inputRequired &&
            <FormSection>
              <FormSectionHeader>
                <FormSectionTitle>Vozidlo</FormSectionTitle>

                <FormSectionDescription>Doplňte informace o vozidle pro připojištění asistence.</FormSectionDescription>
              </FormSectionHeader>

              <FormSectionBody>
                <AdditionalVehicleAssistanceInfo />
              </FormSectionBody>
            </FormSection>
          }

          <ErrorSummary errors={errors} />

          {!isContract &&
            <FormButtons
              rightButtons={
                <>
                  <Button
                    variant="outline"
                    disabled={isSubmitting || isSubmittingNext || isSubmittingCurrent}
                    isLoading={isSubmittingCurrent}
                    type="button"
                    onClick={() => {
                      clearErrors('insurees')
                      setValue('nextPage', 'current')
                      setIsSubmittingCurrent(true)
                      submitForm()
                    }}>
                    Uložit
                  </Button>
                  <Button
                    colorScheme="primary"
                    disabled={isSubmitting || isSubmittingNext || isSubmittingCurrent}
                    isLoading={isSubmittingNext}
                    loadingText="Ukládám..."
                    type="button"
                    onClick={() => {
                      clearErrors('insurees')
                      setValue('nextPage', 'next')
                      setIsSubmittingNext(true)
                      submitForm()
                    }}
                  >
                    <Save />
                    Uložit a pokračovat na shrnutí
                  </Button>

                  <SubmitDialog
                    isOpen={isSavedFormAlertOpen}
                    onClose={savedFormAlertOnClose}
                    header='Uloženo'
                    body={'Údaje byly úspěšně uloženy'}
                  />
                </>
              }
            />
          }
        </MainContent>
      </form>
    </FormProvider>
  )
}
